import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../contexts/auth-provider';
import { SSOProvider, SupabaseProviderConfig } from '../../../types/supabase';
import { EmailStep } from '../../organisms/login-steps/email-step';
import { PasswordStep } from '../../organisms/login-steps/password-step';
import { SSOProvidersStep } from '../../organisms/login-steps/sso-providers-step';

import {
  EmailStepFormValues,
  PasswordStepFormValues,
  SSOProviderStepFormValues,
  useLoginFormValidationSchema,
} from './schema/login-schema';

type LoginStep = 'email' | 'password' | 'ssoProvider';

export const Login = () => {
  const { t } = useTranslation(['auth', 'common']);

  const auth = useAuth();
  const { returnUrl } = useSearch({ from: '/(auth)/login' });
  const decodedReturnUrl = decodeURIComponent(returnUrl);

  const [step, setStep] = useState<LoginStep>('email');
  const [isAuth0Submitting, setIsAuth0Submitting] = useState(false);
  const [ssoProviders, setSsoProviders] = useState<SSOProvider[]>([]);

  const {
    emailStepValidationSchema,
    passwordStepValidationSchema,
    ssoProviderStepValidationSchema,
  } = useLoginFormValidationSchema();

  const resolvers = {
    email: zodResolver(emailStepValidationSchema),
    password: zodResolver(passwordStepValidationSchema),
    ssoProvider: zodResolver(ssoProviderStepValidationSchema),
  };

  const form = useForm({
    resolver: resolvers[step],
    mode: 'onBlur',
    shouldUnregister: false,
    defaultValues: {
      email: '',
      password: '',
      ssoProviderId: '',
    },
  });

  const buildSsoRedirectUrl = () => {
    const loginUrl = new URL('/login', window.location.origin);
    loginUrl.searchParams.set('returnUrl', returnUrl);

    return loginUrl.toString();
  };

  const initiateSSOLogin = async (provider: SupabaseProviderConfig) => {
    if (!provider.ssoClient) {
      throw new Error('Provider is not a SSO client');
    }

    const hasMultipleSSOProviders = provider.ssoProvider.length > 1;

    if (hasMultipleSSOProviders) {
      setSsoProviders(provider.ssoProvider);
      setStep('ssoProvider');
      return;
    }

    const redirectUrl = await auth.loginWithSupabaseSSO(
      provider.ssoProvider[0].id,
      buildSsoRedirectUrl(),
    );

    if (!redirectUrl) {
      throw new Error('SSO redirect URL is missing');
    }

    window.location.href = redirectUrl;
  };

  const onEmailSubmit = async ({ email }: EmailStepFormValues) => {
    form.clearErrors('root');

    const provider = auth.resolveSupabaseProviderForLoginEmail(email);

    if (provider.ssoClient) {
      await initiateSSOLogin(provider);
      return;
    }

    setStep('password');
  };

  const onPasswordSubmit = async ({ email, password }: PasswordStepFormValues) => {
    try {
      await auth.loginWithSupabase(email, password);
    } catch {
      form.setError('root', { message: t('auth:invalidCredentials') });
    }
  };

  const onSSOSubmit = async ({ ssoProviderId }: SSOProviderStepFormValues) => {
    try {
      const redirectUrl = await auth.loginWithSupabaseSSO(ssoProviderId, buildSsoRedirectUrl());

      if (!redirectUrl) {
        throw new Error('SSO redirect URL is missing');
      }

      window.location.href = redirectUrl;
    } catch {
      form.setError('root', { message: t('common:somethingWentWrong') });
    }
  };

  const handleAuth0Login = async () => {
    try {
      form.clearErrors('root');
      setIsAuth0Submitting(true);
      await auth.loginWithAuth0(decodedReturnUrl);
    } catch {
      form.setError('root', { message: t('common:somethingWentWrong') });
    } finally {
      setIsAuth0Submitting(false);
    }
  };

  const handleBackButtonClick = () => {
    form.clearErrors('root');
    setStep('email');
  };

  if (!auth.session) {
    return (
      <FormProvider {...form}>
        {step === 'email' && (
          <EmailStep
            onSubmit={onEmailSubmit}
            onAuth0Login={handleAuth0Login}
            isAuth0Submitting={isAuth0Submitting}
          />
        )}
        {step === 'password' && (
          <PasswordStep onSubmit={onPasswordSubmit} onBackButtonClick={handleBackButtonClick} />
        )}
        {step === 'ssoProvider' && (
          <SSOProvidersStep
            onSubmit={onSSOSubmit}
            ssoProviders={ssoProviders}
            onBackButtonClick={handleBackButtonClick}
          />
        )}
      </FormProvider>
    );
  }

  return <Navigate to={decodedReturnUrl} replace />;
};
